const ACTIVE_OFFICE_STAGES = new Set(["operational", "dormant"]);
const RETIRED_OFFICE_STAGES = new Set(["closed", "retired"]);
const WIND_DOWN_STATUSES = new Set(["wind_down", "wind-down", "winding_down", "exit", "exit_hold"]);

function parseDateOnly(value) {
  if (!value) return null;
  const raw = String(value).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const date = new Date(`${raw}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function isPastExpiryDate(value, today = new Date()) {
  const expiry = parseDateOnly(value);
  if (!expiry) return false;
  const currentDay = new Date(today);
  currentDay.setHours(0, 0, 0, 0);
  return expiry < currentDay;
}

export function formatLongExpiryDate(value) {
  const date = parseDateOnly(value);
  return date
    ? date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "—";
}

function hasRecordedRent(lease, recordedRent) {
  const values = recordedRent !== undefined
    ? [recordedRent]
    : [
    lease?.annualRentUsd,
    lease?.listDisplayRentUsd,
    lease?._listRentUsd,
    lease?.action_value,
    ];
  return values.some((value) => value != null && Number.isFinite(Number(value)) && Number(value) > 0);
}

/**
 * Whether a past expiry still needs attention on the lease or office surface.
 * Active-office expiries can be Holdovers; vacated/wind-down leases remain
 * actionable only while recorded rent is still being carried.
 */
export function isActionablePastDueExpiry(lease, {
  date = lease?.endDate,
  officeStage = lease?.locationStage,
  recordedRent,
} = {}) {
  if (!lease || !isPastExpiryDate(date)) return false;

  const stage = String(officeStage ?? "").toLowerCase();
  if (RETIRED_OFFICE_STAGES.has(stage)) return false;

  const status = String(lease.status ?? "").toLowerCase();
  if (status === "draft") return false;

  const leaseStatus = String(lease.leaseStatus ?? "").toLowerCase();
  const isWindDown = WIND_DOWN_STATUSES.has(leaseStatus)
    || lease.action_window_bucket === "reduce-exposure"
    || lease.posture === "winding-down"
    || lease.posture === "sublease";

  if (stage === "vacated" || isWindDown) {
    return status !== "expired" && hasRecordedRent(lease, recordedRent);
  }

  // Expired-status leases in an active office are genuine Holdovers; in a
  // closed office they were excluded above as off-radar.
  return ACTIVE_OFFICE_STAGES.has(stage)
    && ["active", "holdover", "in_negotiation", "expired"].includes(status);
}